import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { AppDispatch } from '@/app/store/store';
import { CheckoutFlowLayout } from '@/app/Layouts/CheckoutFlowLayout';
import { paymentBuild } from '@/features/payment/config/payment.build';
import PayViaLinkPage from '@/pages/pay-via-link';
import {
  readPaymentContextSession,
  savePaymentContextSession,
} from '@/features/payment/presentation/utils/paymentContextSession';
import {
  normalizePaymentStatus,
  PAYMENT_FAILURE_STATUSES,
  PAYMENT_SUCCESS_STATUSES,
} from '@/features/payment/constants/paymentStatus';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 60000;

const buildResultUrl = (path: string, paymentSource: string, txnId: string): string => {
  const params = new URLSearchParams({
    paymentSource,
    txnId,
  });

  return `${path}?${params.toString()}`;
};

export const PaymentContextPollingPage: React.FC = () => {
  const { encryptedTxnId } = useParams<{ encryptedTxnId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { service } = useMemo(() => paymentBuild(dispatch), [dispatch]);
  const session = useMemo(() => readPaymentContextSession(), []);
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const hasStartedPollingRef = useRef(false);
  const pollingParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const txnId = session?.txnId || pollingParams.get('txnId') || encryptedTxnId || '';
  const paymentSource = session?.paymentSource || pollingParams.get('paymentSource') || 'PAY_VIA_LINK';
  const retryPath = session?.retryPath || '/payment-context';
  const latestTxnIdRef = useRef(txnId);
  const latestPaymentSourceRef = useRef(paymentSource);
  const latestExpiresAtRef = useRef<number | undefined>(session?.expiresAt);

  const syncVerificationMetadata = useCallback((response: {
    txnId?: string;
    paymentSource?: string;
    expiresAt?: number;
  }) => {
    if (response.txnId) latestTxnIdRef.current = response.txnId;
    if (response.paymentSource) latestPaymentSourceRef.current = response.paymentSource;
    if (response.expiresAt) latestExpiresAtRef.current = response.expiresAt;

    if (!session) return;

    savePaymentContextSession({
      ...session,
      txnId: latestTxnIdRef.current || session.txnId,
      paymentSource: latestPaymentSourceRef.current || session.paymentSource,
      ...(latestExpiresAtRef.current ? { expiresAt: latestExpiresAtRef.current } : {}),
    });
  }, [session]);

  const goToFailurePage = useCallback((reason: string) => {
    const resultTxnId = latestTxnIdRef.current || txnId;
    const resultPaymentSource = latestPaymentSourceRef.current || paymentSource;

    navigate(buildResultUrl('/payment-context/failure', resultPaymentSource, resultTxnId), {
      replace: true,
      state: { reason, retryPath },
    });
  }, [navigate, paymentSource, retryPath, txnId]);

  useEffect(() => {
    if (hasStartedPollingRef.current) return;
    hasStartedPollingRef.current = true;

    if (!txnId) {
      goToFailurePage('Transaction details are not available.');
      return;
    }

    let cancelled = false;
    let timeoutId: number | undefined;
    const startedAt = Date.now();

    const poll = async () => {
      if (cancelled) return;

      const elapsedMs = Date.now() - startedAt;
      setSecondsElapsed(Math.min(Math.floor(elapsedMs / 1000), 60));

      try {
        const response = await service.verifyPaymentContext(txnId);
        syncVerificationMetadata(response);
        const status = normalizePaymentStatus(response.paymentStatusStr || 'PENDING');
        const responseTxnId = response.txnId || latestTxnIdRef.current || txnId;
        const responsePaymentSource = response.paymentSource || latestPaymentSourceRef.current || paymentSource;

        if (PAYMENT_SUCCESS_STATUSES.has(status)) {
          navigate(buildResultUrl('/payment-context/success', responsePaymentSource, responseTxnId), {
            replace: true,
          });
          return;
        }

        if (PAYMENT_FAILURE_STATUSES.has(status)) {
          goToFailurePage('Payment could not be completed.');
          return;
        }

      } catch {
        // Keep polling until the one-minute window closes.
      }

      if (Date.now() - startedAt >= MAX_POLL_DURATION_MS) {
        goToFailurePage('Payment verification timed out.');
        return;
      }

      timeoutId = window.setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [goToFailurePage, navigate, paymentSource, service, syncVerificationMetadata, txnId]);

  const processingOverlay = (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-[#011E38]/25 px-4">
      <div className="w-full max-w-sm rounded-lg border border-white/70 bg-white px-8 py-7 text-center shadow-2xl">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#F6FAFF] text-[#f45b4d]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
        <h1 className="text-lg font-bold text-[#011E38]">Processing Payment</h1>
        <p className="mt-2 text-xs font-medium text-gray-500">
          Please wait while we confirm your payment.
        </p>
        <p className="mt-4 text-[10px] font-bold uppercase text-gray-400">
          Verifying for {secondsElapsed}s
        </p>
      </div>
    </div>
  );

  if (!session?.paymentContextQuery) {
    return (
      <div className="relative min-h-screen bg-[#F6FAFF]">
        <div className="pointer-events-none select-none blur-sm">
          <CheckoutFlowLayout
            className="min-h-screen bg-[#F6FAFF] flex flex-col justify-between"
            footerClassName="mt-12"
          >
            <div className="max-w-md mx-auto px-4 mt-12">
              <div className="rounded-lg border border-[#0000001A] bg-white p-8 text-center shadow-sm">
                <h2 className="text-xl font-bold text-[#011E38]">Payment Verification</h2>
              </div>
            </div>
          </CheckoutFlowLayout>
        </div>
        {processingOverlay}
      </div>
    );
  }

  return (
    <div className="pay-via-link-page relative min-h-screen bg-[#F6FAFF]">
      <div className="pointer-events-none select-none blur-sm">
        <PayViaLinkPage paymentContextQueryOverride={session.paymentContextQuery} />
      </div>
      {processingOverlay}
    </div>
  );
};

export default PaymentContextPollingPage;
